import { useCallback, useEffect, useState } from 'react';
import { useT } from '@/i18n';
import { api } from '@/api/client';
import {
  GOOD_AREAS,
  MOMENT_HELPERS,
  MOMENT_TRIGGERS,
  type GoodArea,
  type Moment,
  type MomentHelper,
  type MomentInput,
  type MomentKind,
  type MomentSummary,
  type MomentTrigger,
} from '@/api/types';
import { Alert, Button, Chip } from '@/components/ui';
import { cx } from '@/components/ui/cx';

const INTENSITIES = ['small', 'medium', 'big'] as const;
const DURATIONS = ['under5', '5to15', 'over15'] as const;
const MOODS = [1, 2, 3, 4, 5] as const;
const MOOD_FACE: Record<number, string> = { 1: '😣', 2: '🙁', 3: '😐', 4: '🙂', 5: '😄' };

const toggle = <T,>(list: T[], item: T) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
const textArea =
  'mt-1 w-full rounded-md border-[1.5px] border-sage-300 bg-white px-3 py-2 text-body focus:border-lagoon-500 focus:outline-none';

/**
 * "Log a moment" for one child: a hard moment in three taps (Hard moment → size → Save), a good
 * moment, or the day's mood (one tap). Details are optional. Shows a four-week summary so the
 * next doctor or therapist visit starts from what happened at home.
 */
export const MomentLog = ({ childId, childName }: { childId: string; childName: string }) => {
  const t = useT();
  const [kind, setKind] = useState<MomentKind>();
  const [intensity, setIntensity] = useState<(typeof INTENSITIES)[number]>();
  const [duration, setDuration] = useState<(typeof DURATIONS)[number]>();
  const [triggers, setTriggers] = useState<MomentTrigger[]>([]);
  const [helped, setHelped] = useState<MomentHelper[]>([]);
  const [area, setArea] = useState<GoodArea>();
  const [before, setBefore] = useState('');
  const [after, setAfter] = useState('');
  const [note, setNote] = useState('');
  const [details, setDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'saved' | 'failed'>();
  const [moments, setMoments] = useState<Moment[]>([]);
  const [summary, setSummary] = useState<MomentSummary>();

  const load = useCallback(() => {
    api.moments.list(childId).then(
      (res) => {
        setMoments(res.moments);
        setSummary(res.summary);
      },
      () => undefined,
    );
  }, [childId]);
  useEffect(load, [load]);

  const reset = () => {
    setKind(undefined);
    setIntensity(undefined);
    setDuration(undefined);
    setTriggers([]);
    setHelped([]);
    setArea(undefined);
    setBefore('');
    setAfter('');
    setNote('');
    setDetails(false);
  };

  const save = async (input: MomentInput) => {
    setSaving(true);
    setStatus(undefined);
    try {
      await api.moments.add(input);
      reset();
      setStatus('saved');
      load();
    } catch {
      setStatus('failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m: Moment) => {
    try {
      await api.moments.remove(childId, m.momentId);
      load();
    } catch {
      setStatus('failed');
    }
  };

  const describe = (m: Moment) => {
    if (m.kind === 'mood') return `${MOOD_FACE[m.mood ?? 3]} ${t(`moment.mood.${m.mood ?? 3}`)}`;
    if (m.kind === 'good') return `${t('moment.good')}${m.area ? ` · ${t(`moment.area.${m.area}`)}` : ''}`;
    const bits = [m.intensity ? t(`moment.size.${m.intensity}`) : undefined, ...(m.triggers ?? []).map((k) => t(`moment.trigger.${k}`))];
    return `${t('moment.hard')}${bits.filter(Boolean).length ? ` · ${bits.filter(Boolean).join(', ')}` : ''}`;
  };

  return (
    <section aria-labelledby="moments" className="rounded-lg bg-white p-5 shadow-1">
      <h2 id="moments" className="text-[20px]">{t('moment.title')}</h2>
      <p className="mt-1 text-body text-ink-muted">{t('moment.body', { child: childName })}</p>

      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={t('moment.title')}>
        {(['hard', 'good', 'mood'] as const).map((k) => (
          <Chip
            key={k}
            selected={kind === k}
            onToggle={() => {
              reset();
              setKind(kind === k ? undefined : k);
              setStatus(undefined);
            }}
          >
            {t(`moment.${k}`)}
          </Chip>
        ))}
      </div>

      {kind === 'mood' && (
        <div className="mt-4">
          <p className="text-label font-extrabold">{t('moment.moodAsk', { child: childName })}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button
                key={m}
                type="button"
                disabled={saving}
                aria-label={t(`moment.mood.${m}`)}
                onClick={() => void save({ childId, kind: 'mood', mood: m })}
                className="flex h-12 w-12 items-center justify-center rounded-md border-[1.5px] border-sage-300 bg-white text-[24px] hover:border-lagoon-500"
              >
                {MOOD_FACE[m]}
              </button>
            ))}
          </div>
        </div>
      )}

      {kind === 'good' && (
        <div className="mt-4 flex flex-col gap-3">
          <p className="text-label font-extrabold">{t('moment.goodAsk')}</p>
          <div className="flex flex-wrap gap-2">
            {GOOD_AREAS.map((a) => (
              <Chip key={a} selected={area === a} onToggle={() => setArea(area === a ? undefined : a)}>
                {t(`moment.area.${a}`)}
              </Chip>
            ))}
          </div>
          <label className="text-label">
            {t('moment.note')}
            <textarea className={textArea} rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <Button className="self-start" loading={saving} onClick={() => void save({ childId, kind: 'good', area, note })}>
            {t('moment.save')}
          </Button>
        </div>
      )}

      {kind === 'hard' && (
        <div className="mt-4 flex flex-col gap-3">
          <p className="text-label font-extrabold">{t('moment.sizeAsk')}</p>
          <div className="flex flex-wrap gap-2">
            {INTENSITIES.map((i) => (
              <Chip key={i} selected={intensity === i} onToggle={() => setIntensity(intensity === i ? undefined : i)}>
                {t(`moment.size.${i}`)}
              </Chip>
            ))}
          </div>
          {details ? (
            <>
              <p className="text-label font-extrabold">{t('moment.durationAsk')}</p>
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map((d) => (
                  <Chip key={d} selected={duration === d} onToggle={() => setDuration(duration === d ? undefined : d)}>
                    {t(`moment.duration.${d}`)}
                  </Chip>
                ))}
              </div>
              <p className="text-label font-extrabold">{t('moment.triggerAsk')}</p>
              <div className="flex flex-wrap gap-2">
                {MOMENT_TRIGGERS.map((k) => (
                  <Chip key={k} selected={triggers.includes(k)} onToggle={() => setTriggers(toggle(triggers, k))}>
                    {t(`moment.trigger.${k}`)}
                  </Chip>
                ))}
              </div>
              <p className="text-label font-extrabold">{t('moment.helpedAsk')}</p>
              <div className="flex flex-wrap gap-2">
                {MOMENT_HELPERS.map((k) => (
                  <Chip key={k} selected={helped.includes(k)} onToggle={() => setHelped(toggle(helped, k))}>
                    {t(`moment.helped.${k}`)}
                  </Chip>
                ))}
              </div>
              <label className="text-label">
                {t('moment.before')}
                <textarea className={textArea} rows={2} maxLength={200} value={before} onChange={(e) => setBefore(e.target.value)} />
              </label>
              <label className="text-label">
                {t('moment.after')}
                <textarea className={textArea} rows={2} maxLength={200} value={after} onChange={(e) => setAfter(e.target.value)} />
              </label>
            </>
          ) : (
            <Button variant="tertiary" className="self-start" onClick={() => setDetails(true)}>
              {t('moment.addDetails')}
            </Button>
          )}
          <Button
            className="self-start"
            loading={saving}
            onClick={() => void save({ childId, kind: 'hard', intensity, duration, triggers, helped, before, after })}
          >
            {t('moment.save')}
          </Button>
        </div>
      )}

      {status === 'saved' && <p className="mt-3 text-body font-extrabold text-[#2F5B40]" role="status">{t('moment.saved')}</p>}
      {status === 'failed' && (
        <Alert tone="warn" className="mt-3">
          {t('act.saveFailed')}
        </Alert>
      )}

      {summary && (summary.hard > 0 || summary.good > 0 || summary.moodDays > 0) && (
        <div className="mt-5 border-t border-line pt-4 text-body">
          <h3 className="text-label font-extrabold">{t('moment.summaryTitle')}</h3>
          <p className="mt-1 text-ink-soft">
            {t('moment.summaryCounts', { hard: summary.hard, big: summary.bigHard, good: summary.good, days: summary.moodDays })}
          </p>
          {summary.topTriggers.length > 0 && (
            <p className="mt-1 text-ink-soft">
              {t('moment.summaryTriggers', { list: summary.topTriggers.map((x) => t(`moment.trigger.${x.key}`)).join(', ') })}
            </p>
          )}
          {summary.topHelpers.length > 0 && (
            <p className="mt-1 text-ink-soft">
              {t('moment.summaryHelpers', { list: summary.topHelpers.map((x) => t(`moment.helped.${x.key}`)).join(', ') })}
            </p>
          )}
          <p className="mt-2 text-caption text-ink-muted">{t('moment.summaryUse')}</p>
          <ul className="mt-3 flex flex-col gap-1">
            {moments.slice(0, 5).map((m) => (
              <li key={m.momentId} className={cx('flex items-center justify-between gap-3 text-caption')}>
                <span>
                  <span className="text-ink-muted">{m.date.slice(5).split('-').reverse().join('/')}</span> · {describe(m)}
                </span>
                <button type="button" onClick={() => void remove(m)} className="min-h-tap text-caption font-extrabold text-lagoon-600 hover:underline">
                  {t('moment.remove')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
